import { useTranslation } from 'react-i18next'
import type { Clue } from '../types/puzzle'

interface CluePanelProps {
  /** 横向线索 */
  acrossClues: Clue[]
  /** 纵向线索 */
  downClues: Clue[]
  /** 当前选中的线索编号 */
  activeClueNumber?: number
  /** 当前方向 */
  direction: 'across' | 'down'
  /** 线索点击回调 */
  onClueClick?: (clue: Clue, direction: 'across' | 'down') => void
}

/**
 * 线索面板组件
 */
export function CluePanel({
  acrossClues,
  downClues,
  activeClueNumber,
  direction,
  onClueClick
}: CluePanelProps) {
  const { t } = useTranslation()

  /**
   * 渲染单个线索列表
   */
  const renderClueList = (
    clues: Clue[],
    clueDirection: 'across' | 'down',
    titleKey: string
  ) => (
    <div className="mb-6">
      <h3 className="text-xs font-bold text-brand-orange uppercase tracking-widest mb-3 pl-2 border-l-2 border-brand-orange">
        {t(titleKey)}
      </h3>
      <ul className="space-y-2">
        {clues.map((clue) => {
          const isActive =
            clue.number === activeClueNumber &&
            direction === clueDirection

          return (
            <li
              key={`${clueDirection}-${clue.number}`}
              className={`
                flex gap-3 p-3 rounded-lg cursor-pointer transition-all duration-200
                ${isActive
                  ? 'bg-brand-red/20 border border-brand-red/50 shadow-[0_0_10px_rgba(255,51,0,0.2)]'
                  : 'hover:bg-zinc-800 border border-transparent'}
              `}
              onClick={() => onClueClick?.(clue, clueDirection)}
              role="button"
              tabIndex={0}
            >
              <span className={`font-mono font-bold min-w-[24px] ${isActive ? 'text-brand-orange' : 'text-zinc-500'}`}>
                {clue.number}
              </span>
              <span className={`${isActive ? 'text-white' : 'text-zinc-300'}`}>{clue.clue}</span>
            </li>
          )
        })}
      </ul>
    </div>
  )

  return (
    <div className="clue-panel bg-zinc-900/50 border border-zinc-800 rounded-xl p-4 backdrop-blur-sm
                    max-h-[400px] md:max-h-[500px] 
                    h-[250px] md:h-auto
                    max-w-[66vw] md:max-w-none mx-auto
                    overflow-y-auto overflow-x-hidden
                    scrollbar-thin scrollbar-thumb-zinc-700 scrollbar-track-zinc-900">
      {renderClueList(acrossClues, 'across', 'clues.across')}
      {renderClueList(downClues, 'down', 'clues.down')}
    </div>
  )
}

interface CurrentClueProps {
  /** 当前线索 */
  clue: Clue | null
  /** 方向 */
  direction: 'across' | 'down'
}

/**
 * 当前活跃线索显示组件
 */
export function CurrentClue({ clue, direction }: CurrentClueProps) {
  const { t } = useTranslation()

  if (!clue) return null

  return (
    <div className="current-clue bg-gradient-to-r from-brand-dark to-zinc-900 border-l-4 border-brand-red rounded-r-lg p-4 shadow-lg h-full flex flex-col justify-center">
      <div className="flex items-center gap-2 text-brand-orange mb-1">
        <span className="font-mono font-bold text-xl">{clue.number}</span>
        <span className="text-xs font-bold uppercase tracking-wider bg-brand-orange/10 px-2 py-0.5 rounded">
          {direction === 'across' ? t('clues.across') : t('clues.down')}
        </span>
      </div>
      <p className="text-white text-lg font-medium leading-relaxed">{clue.clue}</p>
    </div>
  )
}
